import React, { useEffect } from "react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp } from "@/components/Motion";
import PageHeader from "@/components/PageHeader";
import CTABlock from "@/components/CTABlock";

const structure = [
  { title: "Track every transaction", body: "Wires, earnest money, escrow draws, HUD receipts, all mapped and categorized the same day." },
  { title: "Align trust accounts", body: "Escrow / IOLTA balances reconcile to client ledgers and statements across closings." },
  { title: "Keep everything reconciled", body: "Monthly 3-way reconciliation as standard. No end-of-quarter surprises." },
];

const GHL_SCRIPT_SRC = "https://links.usabookkeepingsolutionsllc.com/js/form_embed.js";

const checklistItems = [
  "Earnest money landing in the wrong account",
  "Escrow ledgers that drift from bank statements",
  "Stale or duplicate wire instructions in QuickBooks",
  "Trust account interest mis-posted between matters",
  "HUD-1 line items missing supporting documentation",
  "Disbursements posted before funds clear",
  "Closing surplus / shortage left unreconciled",
];

export default function RealEstateAttorneys() {
  useEffect(() => {
    const existing = document.querySelector(`script[src="${GHL_SCRIPT_SRC}"]`);
    if (existing) return;
    const s = document.createElement("script");
    s.src = GHL_SCRIPT_SRC;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div data-testid="real-estate-attorneys-page">
      <PageHeader
        eyebrow="Real Estate Attorneys"
        title={<>When Transactions Move Fast, <em className="text-[#BA1A1A] not-italic">Your Books Still Need to Be Exact</em>.</>}
        lead="Closings. Transfers. Escrow activity. Your financials move quickly, and mistakes can happen just as fast."
        image={{
          src: "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg",
          alt: "Luxury mansion with manicured grounds",
        }}
        meta={[
          { label: "Velocity", value: "High" },
          { label: "Escrow", value: "Tracked" },
          { label: "Cadence", value: "Monthly" },
        ]}
      />

      <section className="container-editorial py-24 md:py-32" data-testid="real-estate-body">
        <div className="grid md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-5">
            <Eyebrow>Structure in the Chaos</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
              We bring structure to the chaos.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg text-[#0C1D36]/80 max-w-xl dropcap">
              Real estate practices don't have the luxury of waiting until month-end to
              notice a reconciliation gap. By the time it surfaces, funds have moved and
              records are harder to retrace. We close that window.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#0C1D36]/12">
          {structure.map((s, i) => (
            <FadeUp key={s.title} className="bg-white p-10" data-testid={`re-card-${i}`}>
              <div className="eyebrow-muted">Step 0{i + 1}</div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#0C1D36] mt-4 leading-tight">
                {s.title}
              </h3>
              <p className="text-[#4B5563] mt-5 leading-relaxed">{s.body}</p>
              <div className="h-px w-8 bg-[#BA1A1A] mt-8" />
            </FadeUp>
          ))}
        </div>

        <p className="mt-16 font-serif italic text-2xl text-[#0C1D36]/90 max-w-xl">
          So nothing gets missed.
        </p>
      </section>

      {/* LEAD MAGNET: Escrow & Closing Accuracy Checklist */}
      <section
        className="bg-[#F3EFE9] rule-top rule-bottom"
        data-testid="real-estate-checklist-section"
      >
        <div className="container-editorial py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="md:col-span-5">
              <Eyebrow>Free Download</Eyebrow>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#0C1D36] mt-6">
                The Escrow &amp; Closing Accuracy Checklist.
              </h2>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-[#0C1D36]/90 max-w-md leading-snug">
                7 bookkeeping gaps that delay closings, and how to close them.
              </p>

              <ul className="mt-10 space-y-3 max-w-md">
                {checklistItems.map((item, i) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[15px] text-[#0C1D36]/85"
                  >
                    <span className="mt-2 w-1.5 h-1.5 bg-[#BA1A1A] rounded-full flex-shrink-0" />
                    <span>
                      <span className="font-mono text-xs text-[#4B5563] mr-2">
                        0{i + 1}
                      </span>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-10 text-sm text-[#4B5563] max-w-md">
                A two-minute self-audit for real estate attorneys. Spot the friction points
                before they push a closing.
              </p>
            </div>

            <div className="md:col-span-7" data-testid="real-estate-checklist-form-wrapper">
              <div className="bg-white border border-[#0C1D36]/12 p-2 md:p-3 shadow-sm">
                <iframe
                  src="https://links.usabookkeepingsolutionsllc.com/widget/form/oeParVt8pGS6jSI9y8EG"
                  style={{
                    width: "100%",
                    minHeight: "860px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#FFFFFF",
                  }}
                  id="inline-oeParVt8pGS6jSI9y8EG"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="Real Estate Checklist"
                  data-height="829"
                  data-layout-iframe-id="inline-oeParVt8pGS6jSI9y8EG"
                  data-form-id="oeParVt8pGS6jSI9y8EG"
                  title="Real Estate Checklist"
                  data-testid="real-estate-checklist-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Real Estate Attorneys"
        title="High-velocity practice? Your books should be built for that."
      />
    </div>
  );
}
